<template>
  <div>
    <v-dialog
      v-model="show" width="348" persistent
      content-class="sipua-phone-box">

      <v-card class="sipua-container">
        <v-toolbar class="mb-4">
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-phone</v-icon>
            {{ $t('sipua.title') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="hidePhone()" :disabled="isCalling">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </v-toolbar>

      <v-container fluid>
        <DialField ref="dialField"
          :call-to="callTo"
          :calling="isCalling"
          :lock="!isReady || !isRegistered"
          :hint="callHint"
          :error-msgs="errorMsgs"
          @num-typing="callTo = $event"
          @init-call="initCall" />

        <PhoneMenu
          :is-calling="isCalling"
          @voicemail="callVM"
          @show-calls-log="showCallsLog = !showCallsLog"
          @show-contacts="showContacts = !showContacts"
          @dialpad-toggle="showDialPad = !showDialPad" />

        <DialPad :show="showDialPad"
          :is-calling="isCalling"
          @dialpad-keypress="keyPress($event)"/>

        <v-row align="center" justify="center" class="pa-8">
          <v-btn large rounded color="green" data-testid="call-btn"
            :disabled="callBtnDisabled" @click="initCall">
            <v-icon color="white">mdi-phone</v-icon>
          </v-btn>
        </v-row>
      </v-container>
      </v-card>
    </v-dialog>
    <Contacts :show="showContacts"
              :pattern="callTo"
              @init-call="callNumber({ number: $event })"
              @show-contacts="showContacts = $event"/>

    <CallsLog :show="showCallsLog"
              :pattern="callTo"
              @init-call="callNumber({ number: $event })"
              @show-calls-log="showCallsLog = $event"/>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Sounds from '@/sipua/sounds'
import SIPUA from '@/sipua/SIPUA'
import events from 'Store/events'
// comoponents
import DialPad from './dialpad'
import DialField from './dialfield'
import PhoneMenu from './menu'
import CallsLog from './calls_log'
import Contacts from './contacts'

export default {
  name: 'phone-app',
  data () {
    return {
      errorMsgs: [],
      callHint: '',
      snd: null,
      showDialPad: false,
      showCallsLog: false,
      showContacts: false
    }
  },
  components: { DialPad, DialField, PhoneMenu, CallsLog, Contacts },
  computed: {
    ...mapGetters({
      phoneShow: 'phoneShow',
      phoneDialNumber: 'phoneDialNumber',
      sipuaStatus: 'sipuaStatus',
      sipuaIsCalling: 'sipua/calling',
      callsLogFilter: 'callslog/filtered',
      phoneNumbers: 'contacts/phoneNumbers/phoneNumbers',
      ctdRead: 'permissions/ctdRead',
      confRead: 'permissions/confRead',
      featureCodesWithTarget: 'contacts/phoneNumbers/featureCodesWithTarget'
    }),
    show: {
      get: vm => vm.phoneShow,
      set: function (val) {
        this.setSIPUAPhoneDisplay(val)
      }
    },
    callTo: {
      get: vm => vm.phoneDialNumber,
      set: function (val) {
        this.setSIPUADialNumber(val)
      }
    },
    isRegistered: vm => vm.sipuaStatus === 3,
    isReady: () => true,
    isCallNumValid: vm => SIPUA.isValidNum(vm.callTo),
    isCalling: vm => vm.sipuaIsCalling,
    callBtnDisabled () {
      return !this.isRegistered || this.isCalling || this.errorMsgs.length > 0
    },
    featureCodes () {
      return this.phoneNumbers.filter(phoneNumber => {
        return phoneNumber.name === 'feature_code'
      })
    },
    featureCodeValues () {
      return this.featureCodes.map(phoneNumberFeatureCode => {
        return phoneNumberFeatureCode.phoneNumber
      })
    },
    featureCodeWithTargetValues () {
      const values = this.featureCodesWithTarget.map(featureCodeAttribute => {
        const fc = this.featureCodes.find(featureCode => featureCode.label === featureCodeAttribute)
        if (fc) {
          return fc.phoneNumber
        }
      })
      return values.filter(n => n)
    }
  },
  mounted () {
    events.subDialerCallback((number) => {
      if (this.ctdRead && this.confRead) {
        this.callNumber({ number: number, allowShow: true })
      }
    })

    this.snd = new Sounds()
    this.callHint = this.$t('sipua.hint.ready')
  },
  watch: {
    show: function (newVal, oldVal) {
      if (newVal) {
        this.getPhoneNumbers()
      }
      this.showCallsLog = newVal && this.canShowCallsLog(this.callTo)
      this.showContacts = newVal && this.canShowContact(this.callTo)
    },
    callTo: function (newVal, oldVal) {
      this.errorMsgs.splice(0)
      this.showCallsLog = this.show && this.canShowCallsLog(newVal)
      this.showContacts = this.show && this.canShowContact(newVal)

      // check allowed feature codes
      if (newVal.startsWith('*') || newVal.startsWith('#')) {
        // let's check if it's on the feature code list.
        if (!this.featureCodeValues.includes(newVal)) {
          // Now, it may have a target value, let's check that.
          const matchFound = this.featureCodeWithTargetValues.some(code => newVal.startsWith(code))
          if (!matchFound) {
            this.errorMsgs.push(this.$t('sipua.error.invalidFeatureCode'))
          }
        }
      }

      if (!SIPUA.isValidNum(newVal) && newVal !== '') {
        this.errorMsgs.push(this.$t('sipua.error.invalidNumber'))
      }

      this.focus()
    },
    isCalling: function (newVal, oldVal) {
      if (newVal === false && oldVal === true) {
        this.show = false
        this.callHint = this.$t('sipua.hint.ready')
        this.callTo = ''
      }
    },
    showDialPad: function (newVal, oldVal) {
      this.focus()
    },
    showContacts: function (newVal, oldVal) {
      this.focus()
    },
    showCallsLog: function (newVal, oldVal) {
      this.focus()
    }
  },
  methods: {
    ...mapActions({
      setSIPUAPhoneDisplay: 'setSIPUAPhoneDisplay',
      setSIPUADialNumber: 'setSIPUADialNumber',
      callNumber: 'callNumber',
      getPhoneNumbers: 'contacts/phoneNumbers/get'
    }),
    focus: function () {
      return this.$refs?.dialField?.focus()
    },
    hidePhone: function () {
      this.show = false
      this.callTo = ''
    },
    keyPress: function (key) {
      if (!this.isReady) return

      this.callTo = this.isCallNumValid
        ? this.callTo + key
        : key
      this.snd.DTMF(key)
    },
    errorHint: function (msg, argv) {
      this.errorMsgs.push(this.$t(msg, argv))
    },
    initCall: function () {
      if (!this.isRegistered) {
        this.errorHint('sipua.hint.notReady')
        return
      }
      if (!this.isCallNumValid) {
        this.errorHint('sipua.hint.missNum')
        return
      }
      this.errorMsgs.splice(0)
      this.callHint = this.$t('sipua.hint.calling', { to: this.callTo })
      this.callNumber({ number: this.callTo })
    },
    canShowCallsLog: function (val) {
      return this.isReady &&
        !this.isCalling &&
        val?.length > 0 &&
        this.callsLogFilter(val).length > 0
    },
    canShowContact: function (val) {
      return this.isReady &&
        !this.isCalling &&
        val?.length > 0
    },
    callVM: function () {
      const fc = this.featureCodes.find(featureCode => featureCode.name === 'myvm')
      this.callNumber({ number: fc?.phoneNumber || '*97' })
    }
  }
}
</script>

<style>
.clid-name { max-width: 150px }
.dialpad-btn button {margin: 0}
.dialpad-btn button .v-btn__content {justify-content: normal}
.dialpad-btn .key-asterisk {font-size: 2.0em; position: absolute; margin-top: -8px}
</style>
